// src/pages/finance-portal/income-tax/IncomeTax.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function IncomeTax() {
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState(null);
    const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());

    // Adjustments state
    const [adjustments, setAdjustments] = useState([]);
    const [adjTotal, setAdjTotal] = useState(0);
    const [adjPage, setAdjPage] = useState(0);
    const [adjRowsPerPage, setAdjRowsPerPage] = useState(20);
    const [adjLoading, setAdjLoading] = useState(true);

    // Adjustment form
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        tax_year: '', adjustment_type: '', direction: 'ADD_BACK',
        description: '', book_amount: '', tax_amount: '',
    });
    const [saving, setSaving] = useState(false);

    // Book-to-Tax summary
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    const fetchAdjustments = useCallback(async () => {
        setAdjLoading(true);
        setError(null);
        try {
            const params = { page: adjPage + 1, per_page: adjRowsPerPage };
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/adjustments', { params });
            if (res.data.success) {
                setAdjustments(res.data.data || []);
                setAdjTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load adjustments');
        } finally {
            setAdjLoading(false);
        }
    }, [adjPage, adjRowsPerPage, taxYear]);

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const params = {};
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/adjustments/book-to-tax-summary', { params });
            if (res.data.success) {
                setSummary(res.data.data || null);
            }
        } catch { /* silent */ }
        finally { setSummaryLoading(false); }
    }, [taxYear]);

    useEffect(() => {
        if (activeTab === 0) fetchAdjustments();
        else fetchSummary();
    }, [activeTab, fetchAdjustments, fetchSummary]);

    const openAddForm = () => {
        setEditingId(null);
        setFormData({
            tax_year: taxYear, adjustment_type: '', direction: 'ADD_BACK',
            description: '', book_amount: '', tax_amount: '',
        });
        setFormOpen(true);
    };

    const openEditForm = (adj) => {
        setEditingId(adj.id);
        setFormData({
            tax_year: adj.tax_year?.toString() || taxYear,
            adjustment_type: adj.adjustment_type || '',
            direction: adj.direction || 'ADD_BACK',
            description: adj.description || '',
            book_amount: adj.book_amount?.toString() || '',
            tax_amount: adj.tax_amount?.toString() || '',
        });
        setFormOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                tax_year: parseInt(formData.tax_year, 10),
                book_amount: parseFloat(formData.book_amount) || 0,
                tax_amount: parseFloat(formData.tax_amount) || 0,
            };
            if (editingId) {
                await axiosInstance.put(`/api/finance/tax/adjustments/${editingId}`, payload);
                enqueueSnackbar('Adjustment updated', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/tax/adjustments', payload);
                enqueueSnackbar('Adjustment created', { variant: 'success' });
            }
            setFormOpen(false);
            fetchAdjustments();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save adjustment', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this adjustment?')) return;
        try {
            await axiosInstance.delete(`/api/finance/tax/adjustments/${id}`);
            enqueueSnackbar('Adjustment deleted', { variant: 'success' });
            fetchAdjustments();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Income Tax</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Book-to-tax adjustments and reconciliation</Typography>
                        </Box>
                    </Box>
                    {activeTab === 0 && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAddForm}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Adjustment
                        </Button>
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Tax Year Filter */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setAdjPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                </Box>

                {/* Tabs */}
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 1,
                    '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                    '& .Mui-selected': { color: ACCENT },
                    '& .MuiTabs-indicator': { bgcolor: ACCENT },
                }}>
                    <Tab label="Adjustments" />
                    <Tab label="Book-to-Tax Summary" />
                </Tabs>

                {/* Adjustments Tab */}
                <TabPanel value={activeTab} index={0}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Tax Year', 'Type', 'Direction', 'Description', 'Book Amount', 'Tax Amount', 'Actions'].map(h => (
                                            <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {adjLoading ? (
                                        [1, 2, 3, 4, 5].map(i => (
                                            <TableRow key={i}>{[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                        ))
                                    ) : adjustments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No adjustments found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : adjustments.map(adj => (
                                        <TableRow key={adj.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>{adj.tax_year}</TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{adj.adjustment_type || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={adj.direction === 'ADD_BACK' ? 'Add Back' : 'Subtract'} size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                        bgcolor: adj.direction === 'ADD_BACK' ? '#fef3c7' : '#dbeafe',
                                                        color: adj.direction === 'ADD_BACK' ? '#92400e' : '#1e40af',
                                                    }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{adj.description || '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatCurrency(adj.book_amount)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{formatCurrency(adj.tax_amount)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => openEditForm(adj)}>
                                                            <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(adj.id)}>
                                                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div" count={adjTotal} page={adjPage}
                            onPageChange={(_, p) => setAdjPage(p)}
                            rowsPerPage={adjRowsPerPage}
                            onRowsPerPageChange={e => { setAdjRowsPerPage(parseInt(e.target.value, 10)); setAdjPage(0); }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ '& *': { fontFamily } }}
                        />
                    </Card>
                </TabPanel>

                {/* Book-to-Tax Summary Tab */}
                <TabPanel value={activeTab} index={1}>
                    {summaryLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
                    ) : !summary ? (
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No summary data available for this year</Typography>
                        </Card>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {[
                                    { label: 'Book Income', value: summary.book_income, color: '#1e40af' },
                                    { label: '+ Additions', value: summary.total_additions, color: '#92400e' },
                                    { label: '- Subtractions', value: summary.total_subtractions, color: '#dc2626' },
                                    { label: '= Taxable Income', value: summary.taxable_income, color: '#166534' },
                                ].map((card, idx) => (
                                    <Grid item xs={6} md={3} key={idx}>
                                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2.5 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>{card.label}</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: card.color, mt: 0.5 }}>{formatCurrency(card.value)}</Typography>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Additions Table */}
                            {summary.additions && summary.additions.length > 0 && (
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                                    <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>Additions (Add Back)</Typography>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                    {['Type', 'Description', 'Book Amount', 'Tax Amount'].map(h => (
                                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.additions.map((a, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{a.adjustment_type}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{a.description || '\u2014'}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{formatCurrency(a.book_amount)}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{formatCurrency(a.tax_amount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            )}

                            {/* Subtractions Table */}
                            {summary.subtractions && summary.subtractions.length > 0 && (
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                    <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>Subtractions</Typography>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                    {['Type', 'Description', 'Book Amount', 'Tax Amount'].map(h => (
                                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.subtractions.map((s, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{s.adjustment_type}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{s.description || '\u2014'}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{formatCurrency(s.book_amount)}</TableCell>
                                                        <TableCell sx={bodyCellSx}>{formatCurrency(s.tax_amount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            )}
                        </>
                    )}
                </TabPanel>
            </Box>

            {/* Add/Edit Adjustment Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{editingId ? 'Edit Adjustment' : 'Add Adjustment'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Tax Year" type="number" required
                                value={formData.tax_year}
                                onChange={e => setFormData(prev => ({ ...prev, tax_year: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Adjustment Type" required
                                value={formData.adjustment_type}
                                onChange={e => setFormData(prev => ({ ...prev, adjustment_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth select label="Direction" required
                                value={formData.direction}
                                onChange={e => setFormData(prev => ({ ...prev, direction: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                <MenuItem value="ADD_BACK" sx={{ fontFamily }}>Add Back</MenuItem>
                                <MenuItem value="SUBTRACT" sx={{ fontFamily }}>Subtract</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Description" multiline rows={2}
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Book Amount" type="number"
                                value={formData.book_amount}
                                onChange={e => setFormData(prev => ({ ...prev, book_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Tax Amount" type="number"
                                value={formData.tax_amount}
                                onChange={e => setFormData(prev => ({ ...prev, tax_amount: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving || !formData.adjustment_type}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
